// Shared types for Delegation Bench (frontend + hints for worker)

export type NodeKind =
  | 'delegation'
  | 'human_responsibility'
  | 'assumption'
  | 'evidence'
  | 'decision'
  | 'open_question'
  | 'system_constraint'

export type Uncertainty = 'negligible' | 'manageable' | 'high' | 'critical' | null

export interface MapNode {
  id: string
  map_id: string
  kind: NodeKind
  label: string
  x: number
  y: number
  uncertainty: Uncertainty
  known: string | null
  unknown: string | null
  evidence_need: string | null
  next_question: string | null
  next_owner_hint: string | null
  created_at: number
  updated_at: number
}

export interface MapEdge {
  id: string
  map_id: string
  source_id: string
  target_id: string
  label: string | null
  created_at: number
}

export interface Journey {
  id: string
  map_id: string
  node_id: string | null
  due_at: number
  note: string | null
  status: 'pending' | 'due' | 'completed' | 'cancelled'
  created_at: number
  completed_at: number | null
}

export interface Proposal {
  id: string
  map_id: string
  kind: NodeKind
  label: string
  uncertainty: Uncertainty
  known: string | null
  unknown: string | null
  evidence_need: string | null
  next_question: string | null
  next_owner_hint: string | null
  source_excerpt: string | null
  status: 'proposed' | 'accepted' | 'dismissed'
  created_at: number
}

export interface ResponsibilityMap {
  id: string
  title: string
  scenario: string | null
  created_at: number
  updated_at: number
  cast_at: number | null
  status: string
}

export interface FullMap {
  map: ResponsibilityMap
  nodes: MapNode[]
  edges: MapEdge[]
  journeys: Journey[]
  proposals: Proposal[]
}

export interface MapPulse {
  total_nodes: number
  uncertainty_labeled: number
  four_fields_complete: number
  completion_pct: number
  critical_knots: number
  high_knots: number
  ai_delegations: number
  open_journeys: number
}

export const UNCERTAINTY_LABELS: Uncertainty[] = ['negligible', 'manageable', 'high', 'critical']

export const NODE_KINDS: { value: NodeKind; label: string; hint: string }[] = [
  { value: 'delegation', label: 'AI Delegation', hint: 'Work handed to an AI system' },
  { value: 'human_responsibility', label: 'Human Owner', hint: 'Who ultimately owns the outcome' },
  { value: 'assumption', label: 'Assumption', hint: 'Something taken as true without evidence' },
  { value: 'evidence', label: 'Evidence / Source', hint: 'A concrete fact or document' },
  { value: 'decision', label: 'Decision Point', hint: 'A choice that was or needs to be made' },
  { value: 'open_question', label: 'Open Question', hint: 'The live uncertainty or inquiry' },
  { value: 'system_constraint', label: 'System Constraint', hint: 'Policy, funder rule, timeline, capacity' },
]
